import React, { useEffect, useRef, useState } from "react";
import { FaDownload, FaFileAudio } from "react-icons/fa";
import { ClipLoader } from "react-spinners";
import { Storage } from "appwrite";
import client from "./appwriteConfig";
import "./css/ExportSong.css";

const storage = new Storage(client);

const NOTE_NAMES = [
  "C",
  "C#",
  "D",
  "D#",
  "E",
  "F",
  "F#",
  "G",
  "G#",
  "A",
  "A#",
  "B",
];

const generateNotes = () => {
  const result = [];

  for (let octave = 0; octave < 6; octave += 1) {
    NOTE_NAMES.forEach((note) => {
      result.push(`${note}${octave}`);
    });
  }

  return result;
};

const NOTES = generateNotes();

const clamp = (value, minimum, maximum) => {
  const number = Number(value);

  if (!Number.isFinite(number)) {
    return minimum;
  }

  return Math.min(maximum, Math.max(minimum, number));
};

const normalizeKitName = (value = "") =>
  String(value)
    .toLowerCase()
    .replace(/[^a-z0-9]/g, "");

const getKitForPad = (patternKits, pad) => {
  if (!patternKits || !pad) return null;

  if (patternKits[pad.kitName]) {
    return patternKits[pad.kitName];
  }

  const normalizedPadName = normalizeKitName(pad.kitName);

  const matchingEntry = Object.entries(patternKits).find(([kitName]) => {
    return normalizeKitName(kitName) === normalizedPadName;
  });

  return matchingEntry?.[1] || null;
};

const getPatternStepCount = (patternNumber, patternCache) => {
  if (!patternNumber) return 16;

  return Number(patternCache?.[String(patternNumber)]?.stepCount) || 16;
};

const getVocalStepCount = (vocalId, vocalPatternCache) => {
  if (!vocalId) return 16;

  return Number(vocalPatternCache?.[String(vocalId)]?.stepCount) || 16;
};

const getTrackTotalSteps = (selectedItems, getItemStepCount) => {
  return selectedItems.reduce((total, item) => {
    return total + (item ? getItemStepCount(item) : 16);
  }, 0);
};

const getTimelineItems = (selectedItems, getItemStepCount) => {
  let currentStartStep = 0;

  return selectedItems.map((item, index) => {
    const stepCount = item ? getItemStepCount(item) : 16;

    const timelineItem = {
      index,
      item,
      stepCount,
      startStep: currentStartStep,
      endStep: currentStartStep + stepCount,
    };

    currentStartStep += stepCount;

    return timelineItem;
  });
};

const fetchArrayBuffer = async (url) => {
  const response = await fetch(url);

  if (!response.ok) {
    throw new Error(
      `Unable to fetch audio: ${response.status} ${response.statusText}`,
    );
  }

  return response.arrayBuffer();
};

const decodeAudio = async (decodingContext, arrayBuffer) => {
  return decodingContext.decodeAudioData(arrayBuffer.slice(0));
};

const createGainConnection = (context, source, destination, volume) => {
  const gain = context.createGain();

  gain.gain.value = clamp(volume, 0, 2);

  source.connect(gain);
  gain.connect(destination);

  return gain;
};

const scheduleAudioBuffer = ({
  context,
  buffer,
  destination,
  startTime,
  volume = 1,
  playbackRate = 1,
  duration,
  offset = 0,
}) => {
  if (!buffer) return null;

  const source = context.createBufferSource();

  source.buffer = buffer;
  source.playbackRate.value = playbackRate;

  createGainConnection(context, source, destination, volume);

  const safeStartTime = Math.max(0, startTime);
  const safeOffset = Math.max(0, offset);

  if (duration && duration > 0) {
    const availableDuration = Math.max(0.01, buffer.duration - safeOffset);

    source.start(
      safeStartTime,
      safeOffset,
      Math.min(duration, availableDuration),
    );
  } else {
    source.start(safeStartTime, safeOffset);
  }

  return source;
};

const audioBufferToWavBlob = (audioBuffer) => {
  const numberOfChannels = audioBuffer.numberOfChannels;

  const sampleRate = audioBuffer.sampleRate;
  const bytesPerSample = 2;
  const blockAlign = numberOfChannels * bytesPerSample;

  const dataLength = audioBuffer.length * blockAlign;

  const arrayBuffer = new ArrayBuffer(44 + dataLength);

  const view = new DataView(arrayBuffer);

  const writeString = (offset, value) => {
    for (let index = 0; index < value.length; index += 1) {
      view.setUint8(offset + index, value.charCodeAt(index));
    }
  };

  writeString(0, "RIFF");
  view.setUint32(4, 36 + dataLength, true);
  writeString(8, "WAVE");
  writeString(12, "fmt ");

  view.setUint32(16, 16, true);
  view.setUint16(20, 1, true);
  view.setUint16(22, numberOfChannels, true);
  view.setUint32(24, sampleRate, true);

  view.setUint32(28, sampleRate * blockAlign, true);

  view.setUint16(32, blockAlign, true);
  view.setUint16(34, 16, true);

  writeString(36, "data");
  view.setUint32(40, dataLength, true);

  const channels = [];

  for (let channel = 0; channel < numberOfChannels; channel += 1) {
    channels.push(audioBuffer.getChannelData(channel));
  }

  let offset = 44;

  for (
    let sampleIndex = 0;
    sampleIndex < audioBuffer.length;
    sampleIndex += 1
  ) {
    for (let channel = 0; channel < numberOfChannels; channel += 1) {
      const sample = Math.max(-1, Math.min(1, channels[channel][sampleIndex]));

      view.setInt16(
        offset,
        sample < 0 ? sample * 0x8000 : sample * 0x7fff,
        true,
      );

      offset += bytesPerSample;
    }
  }

  return new Blob([arrayBuffer], {
    type: "audio/wav",
  });
};

const createSafeFilename = (name) => {
  const cleaned = String(name || "Rendered Song")
    .trim()
    .replace(/[^a-zA-Z0-9 _-]/g, "")
    .replace(/\s+/g, "_");

  return cleaned || "Rendered_Song";
};

const ExportSong = ({
  exportRequest = 0,
  bpm = 120,
  songName = "",
  songVolume = 0.5,
  pads = [],
  selectedPatterns = [],
  selectedVocalPatterns = [],
  patternCache = {},
  vocalPatternCache = {},
  loadPatternFromCache,
  onExportingChange,
}) => {
  const [isExporting, setIsExporting] = useState(false);

  const [progress, setProgress] = useState(0);
  const [renderedUrl, setRenderedUrl] = useState("");

  const [renderedName, setRenderedName] = useState("");

  const [errorMessage, setErrorMessage] = useState("");

  const previousRequestRef = useRef(0);
  const renderedUrlRef = useRef("");

  useEffect(() => {
    renderedUrlRef.current = renderedUrl;
  }, [renderedUrl]);

  useEffect(() => {
    return () => {
      if (renderedUrlRef.current) {
        URL.revokeObjectURL(renderedUrlRef.current);
      }
    };
  }, []);

  const updateExporting = (value) => {
    setIsExporting(value);
    onExportingChange?.(value);
  };

  const loadAllPatternData = async () => {
    const uniquePatternNumbers = [
      ...new Set(selectedPatterns.filter(Boolean).map(String)),
    ];

    const nextPatternCache = {
      ...patternCache,
    };

    for (let index = 0; index < uniquePatternNumbers.length; index += 1) {
      const patternNumber = uniquePatternNumbers[index];

      if (!nextPatternCache[patternNumber]) {
        if (!loadPatternFromCache) {
          throw new Error(`Pattern ${patternNumber} has not loaded.`);
        }

        const loadedPattern = await loadPatternFromCache(patternNumber);

        if (loadedPattern) {
          nextPatternCache[patternNumber] = loadedPattern;
        }
      }

      setProgress(
        5 +
          Math.round(
            ((index + 1) / Math.max(1, uniquePatternNumbers.length)) * 10,
          ),
      );
    }

    return nextPatternCache;
  };

  const renderSong = async () => {
    if (isExporting) return;

    const hasBeat = selectedPatterns.some(Boolean);
    const hasVocal = selectedVocalPatterns.some(Boolean);

    if (!hasBeat && !hasVocal) {
      setErrorMessage(
        "Add at least one beat or vocal pattern before exporting.",
      );

      return;
    }

    updateExporting(true);
    setProgress(1);
    setErrorMessage("");

    try {
      const safeBpm = clamp(bpm, 20, 400);

      const secondsPerStep = 60 / safeBpm / 4;

      const loadedPatternCache = await loadAllPatternData();

      setProgress(17);

      const beatTimeline = getTimelineItems(selectedPatterns, (patternNumber) =>
        getPatternStepCount(patternNumber, loadedPatternCache),
      );

      const vocalTimeline = getTimelineItems(selectedVocalPatterns, (vocalId) =>
        getVocalStepCount(vocalId, vocalPatternCache),
      );

      const totalBeatSteps = getTrackTotalSteps(
        selectedPatterns,
        (patternNumber) =>
          getPatternStepCount(patternNumber, loadedPatternCache),
      );

      const totalVocalSteps = getTrackTotalSteps(
        selectedVocalPatterns,
        (vocalId) => getVocalStepCount(vocalId, vocalPatternCache),
      );

      const totalSteps = Math.max(totalBeatSteps, totalVocalSteps, 16);

      /*
        Keep a small tail so the last samples
        do not get cut abruptly.
      */
      const tailSeconds = 3;

      const songDuration = totalSteps * secondsPerStep + tailSeconds;

      const sampleRate = 44100;

      const OfflineContextClass =
        window.OfflineAudioContext || window.webkitOfflineAudioContext;

      if (!OfflineContextClass) {
        throw new Error(
          "Offline audio rendering is not supported in this browser.",
        );
      }

      const offlineContext = new OfflineContextClass(
        2,
        Math.ceil(sampleRate * songDuration),
        sampleRate,
      );

      const masterGain = offlineContext.createGain();

      masterGain.gain.value = clamp(songVolume, 0, 1);

      masterGain.connect(offlineContext.destination);

      const AudioContextClass =
        window.AudioContext || window.webkitAudioContext;

      const decodingContext = new AudioContextClass();

      const kitBuffers = new Map();
      const instrumentBuffers = new Map();
      const vocalBuffers = new Map();

      /*
        Load only the drum samples actually
        required by selected patterns.
      */
      const requiredPads = new Map();

      beatTimeline.forEach((slot) => {
        if (!slot.item) return;

        const pattern = loadedPatternCache[String(slot.item)];

        const patternKits = pattern?.kits || {};

        pads.forEach((pad) => {
          const kit = getKitForPad(patternKits, pad);

          const hasActiveSteps =
            Array.isArray(kit?.sequence) && kit.sequence.some(Boolean);

          if (hasActiveSteps && pad.kitUrl) {
            requiredPads.set(String(pad.kitUrl), pad);
          }
        });
      });

      const requiredPadList = [...requiredPads.values()];

      for (let index = 0; index < requiredPadList.length; index += 1) {
        const pad = requiredPadList[index];
        const kitId = String(pad.kitUrl);

        const fileView = await storage.getFileView(
          process.env.REACT_APP_BUCKET_KIT_ID,
          kitId,
        );

        const arrayBuffer = await fetchArrayBuffer(fileView.href);

        const decodedBuffer = await decodeAudio(decodingContext, arrayBuffer);

        kitBuffers.set(kitId, decodedBuffer);

        setProgress(
          18 +
            Math.round(
              ((index + 1) / Math.max(1, requiredPadList.length)) * 18,
            ),
        );
      }

      /*
        Load all required instruments.
      */
      const requiredInstruments = new Set();

      beatTimeline.forEach((slot) => {
        if (!slot.item) return;

        const pattern = loadedPatternCache[String(slot.item)];

        Object.values(pattern?.instruments || {}).forEach((instrument) => {
          if (instrument?.InsName) {
            requiredInstruments.add(instrument.InsName);
          }
        });
      });

      const instrumentNames = [...requiredInstruments];

      for (let index = 0; index < instrumentNames.length; index += 1) {
        const instrumentName = instrumentNames[index];

        const instrumentUrl = `/instruments/${instrumentName.toLowerCase()}.wav`;

        const arrayBuffer = await fetchArrayBuffer(instrumentUrl);

        const decodedBuffer = await decodeAudio(decodingContext, arrayBuffer);

        instrumentBuffers.set(instrumentName, decodedBuffer);

        setProgress(
          37 +
            Math.round(
              ((index + 1) / Math.max(1, instrumentNames.length)) * 16,
            ),
        );
      }

      /*
        Load all selected vocal recordings.
      */
      const selectedVocalIds = [
        ...new Set(selectedVocalPatterns.filter(Boolean).map(String)),
      ];

      for (let index = 0; index < selectedVocalIds.length; index += 1) {
        const vocalId = selectedVocalIds[index];

        const vocal = vocalPatternCache[vocalId];

        if (!vocal?.audioUrl) {
          console.warn(`Skipping vocal ${vocalId}: no audio URL.`);

          continue;
        }

        const arrayBuffer = await fetchArrayBuffer(vocal.audioUrl);

        const decodedBuffer = await decodeAudio(decodingContext, arrayBuffer);

        vocalBuffers.set(vocalId, decodedBuffer);

        setProgress(
          54 +
            Math.round(
              ((index + 1) / Math.max(1, selectedVocalIds.length)) * 14,
            ),
        );
      }

      await decodingContext.close();

      /*
        Schedule beat and instrument timeline.
      */
      beatTimeline.forEach((slot) => {
        if (!slot.item) return;

        const pattern = loadedPatternCache[String(slot.item)];

        if (!pattern) return;

        const patternStartTime = slot.startStep * secondsPerStep;

        const patternKits = pattern.kits || {};

        pads.forEach((pad) => {
          const kit = getKitForPad(patternKits, pad);

          if (!Array.isArray(kit?.sequence)) {
            return;
          }

          const kitBuffer = kitBuffers.get(String(pad.kitUrl));

          if (!kitBuffer) return;

          const kitVolume = clamp(kit.kitVolume ?? kit.volume ?? 0.5, 0, 1.5);

          kit.sequence.forEach((active, stepIndex) => {
            if (!active) return;

            if (stepIndex >= slot.stepCount) {
              return;
            }

            scheduleAudioBuffer({
              context: offlineContext,
              buffer: kitBuffer,
              destination: masterGain,
              startTime: patternStartTime + stepIndex * secondsPerStep,
              volume: kitVolume,
            });
          });
        });

        Object.values(pattern.instruments || {}).forEach((instrument) => {
          if (!instrument?.InsName || !Array.isArray(instrument.notes)) {
            return;
          }

          const instrumentBuffer = instrumentBuffers.get(instrument.InsName);

          if (!instrumentBuffer) return;

          const instrumentVolume = clamp(
            instrument.InsVolume ?? instrument.volume ?? 0.5,
            0,
            1.5,
          );

          instrument.notes.forEach((savedNote) => {
            const [noteName, stepValue] = String(savedNote).split(",");

            const oneBasedStep = Number(stepValue);

            if (
              !Number.isFinite(oneBasedStep) ||
              oneBasedStep < 1 ||
              oneBasedStep > slot.stepCount
            ) {
              return;
            }

            const noteIndex = NOTES.findIndex(
              (note) => note === String(noteName).trim(),
            );

            if (noteIndex === -1) {
              return;
            }

            /*
                Same pitch calculation used by
                InstrumentAudioContext.
              */
            const baseFrequency = 261.63;

            const noteFrequency =
              baseFrequency * Math.pow(2, (noteIndex - NOTES.length + 48) / 12);

            const playbackRate = noteFrequency / baseFrequency;

            const noteStartTime =
              patternStartTime + (oneBasedStep - 1) * secondsPerStep;

            scheduleAudioBuffer({
              context: offlineContext,
              buffer: instrumentBuffer,
              destination: masterGain,
              startTime: noteStartTime,
              volume: instrumentVolume,
              playbackRate,
              /*
                  Match your current approximately
                  300 ms note behavior, while
                  allowing slower BPM notes to
                  breathe slightly longer.
                */
              duration: Math.max(0.3, secondsPerStep * 0.9),
            });
          });
        });
      });

      setProgress(76);

      /*
        Schedule vocals using their independent
        timeline positions.
      */
      vocalTimeline.forEach((slot) => {
        if (!slot.item) return;

        const vocalId = String(slot.item);

        const vocal = vocalPatternCache[vocalId];

        const vocalBuffer = vocalBuffers.get(vocalId);

        if (!vocal || !vocalBuffer) {
          return;
        }

        const vocalStartTime = slot.startStep * secondsPerStep;

        const slotDuration = slot.stepCount * secondsPerStep;

        const vocalVolume = clamp(vocal.volume ?? 1, 0, 1.5);

        /*
          Limit vocal playback to its timeline slot.

          This prevents a long file from overlapping
          the next selected vocal clip.
        */
        const playableDuration = Math.min(vocalBuffer.duration, slotDuration);

        scheduleAudioBuffer({
          context: offlineContext,
          buffer: vocalBuffer,
          destination: masterGain,
          startTime: vocalStartTime,
          volume: vocalVolume,
          duration: playableDuration,
        });
      });

      setProgress(84);

      const renderedBuffer = await offlineContext.startRendering();

      setProgress(94);

      const wavBlob = audioBufferToWavBlob(renderedBuffer);

      if (renderedUrlRef.current) {
        URL.revokeObjectURL(renderedUrlRef.current);
      }

      const nextRenderedUrl = URL.createObjectURL(wavBlob);

      const nextName = `${createSafeFilename(songName)}_${safeBpm}BPM.wav`;

      renderedUrlRef.current = nextRenderedUrl;

      setRenderedUrl(nextRenderedUrl);
      setRenderedName(nextName);
      setProgress(100);
    } catch (error) {
      console.error("Unable to export song:", error);

      setErrorMessage(error?.message || "Unable to render the song.");
    } finally {
      updateExporting(false);
    }
  };

  useEffect(() => {
    if (!exportRequest) return;

    if (previousRequestRef.current === exportRequest) {
      return;
    }

    previousRequestRef.current = exportRequest;

    renderSong();
  }, [exportRequest]);

  return (
    <div className="export-song-output">
      {isExporting && (
        <div className="export-song-progress">
          <div className="export-song-progress-head">
            <ClipLoader size={18} color="#ffffff" />

            <span>Rendering complete song...</span>

            <strong>{progress}%</strong>
          </div>

          <div className="export-song-progress-track">
            <div
              style={{
                width: `${progress}%`,
              }}
            />
          </div>
        </div>
      )}

      {errorMessage && <div className="export-song-error">{errorMessage}</div>}

      {renderedUrl && !isExporting && (
        <div className="rendered-song-card">
          <div className="rendered-song-title">
            <FaFileAudio />

            <div>
              <strong>Final rendered song</strong>

              <span>{renderedName}</span>
            </div>
          </div>

          <audio controls preload="metadata" src={renderedUrl} />

          <a
            href={renderedUrl}
            download={renderedName}
            className="rendered-song-download"
          >
            <FaDownload />
            Download WAV
          </a>
        </div>
      )}
    </div>
  );
};

export default ExportSong;
